import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormStateReturn,
  UseFormWatch,
} from 'react-hook-form';
import AvatarChip from '../../../../../shared/avatar-chip';
import UsersAutocomplete from '../../../../../shared/users-autocomplete';
import { useRef, useState } from 'react';
import { FormSectionHeader } from '../../../../../shared/form-section-header.tsx';
import Button from '@mui/material/Button';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';

interface ActionsStepProps {
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  actionDisplayValue?: string;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  getValues: UseFormGetValues<DecomPolicyFormValues>;
  dayInputErrors: Record<number, string>;
  setDayInputErrors: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >;
  touchedFields: UseFormStateReturn<DecomPolicyFormValues>['touchedFields'];
}

export default function ActionsStep({
  control,
  watch,
  errors,
  setValue,
  clearErrors,
  dayInputErrors,
  setDayInputErrors,
}: ActionsStepProps) {
  const actionsData = watch('actions') ?? [];
  const defaultUserToNotify = watch('defaultUserToNotify') ?? '';
  const defaultUseManagerEmail = watch('defaultUseManagerEmail') ?? null;
  const defaultDestUser = watch('defaultDestUser') ?? null;
  const customDays = watch('customDays') ?? [];
  const defaultRadio: 'manager' | 'custom' | 'no-default' =
    defaultUseManagerEmail === null && defaultDestUser === null
      ? 'no-default'
      : defaultUseManagerEmail === true
      ? 'manager'
      : 'custom';

  const theme = useTheme();
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [
    autoCompleteResponseDefaultUsersToNotify,
    setAutoCompleteResponseDefaultUsersToNotify,
  ] = useState('');
  const [dayInputs, setDayInputs] = useState<string[]>(customDays.map(String));

  const addCustomDay = () => {
    let next = 0;
    while (customDays.includes(next)) next++;

    setDayInputs((prev) => [...prev, String(next)]);
    setValue('customDays', [...customDays, next], { shouldDirty: true });

    const existingActions = actionsData;
    setValue(
      'actions',
      {
        ...existingActions,
        [next]: { day: next, actions: [] },
      },
      { shouldDirty: true }
    );
  };

  const onInputChange = (index: number, value: string) => {
    setDayInputs((prev) => {
      const newInputs = [...prev];
      newInputs[index] = value;
      return newInputs;
    });
  };

  const onDayInputBlur = (index: number) => {
    const inputVal = dayInputs[index].trim();
    const newErrors = { ...dayInputErrors };

    if (inputVal === '') {
      newErrors[index] = 'Day is required';
      setDayInputErrors(newErrors);
      return;
    }

    const dayNum = parseInt(inputVal, 10);
    if (isNaN(dayNum)) {
      newErrors[index] = 'Invalid day';
      setDayInputErrors(newErrors);
      return;
    }

    if (!(dayNum >= 0 && dayNum <= 364)) {
      newErrors[index] = 'Must be a number between 0 to 364.';
      setDayInputErrors(newErrors);
      return;
    }

    const isDuplicate = customDays.some(
      (day, i) => i !== index && day === dayNum
    );
    if (isDuplicate) {
      newErrors[index] = 'Duplicate day';
      setDayInputErrors(newErrors);
      return;
    }

    delete newErrors[index];
    setDayInputErrors(newErrors);

    const oldDay = customDays[index];
    if (oldDay === dayNum) return;

    const newCustomDays = [...customDays];
    newCustomDays[index] = dayNum;
    setValue('customDays', newCustomDays, { shouldDirty: true });

    const newDayInputs = [...dayInputs];
    newDayInputs[index] = String(dayNum);
    setDayInputs(newDayInputs);

    const updatedActions: typeof actionsData = { ...actionsData };
    if (updatedActions[oldDay]) {
      updatedActions[dayNum] = {
        ...updatedActions[oldDay],
        day: dayNum,
      };
      delete updatedActions[oldDay];
    }
    setValue('actions', updatedActions, {
      shouldDirty: true,
    });
  };

  const onRemove = (index: number) => {
    const removedDay = customDays[index];

    // update days + inputs
    const newCustomDays = customDays.filter((_, i) => i !== index);
    setValue('customDays', [...newCustomDays], { shouldDirty: true });
    const newDayInputs = dayInputs.filter((_, i) => i !== index);
    setDayInputs(newDayInputs);

    const existingActions = actionsData;
    const updatedActions = Object.fromEntries(
      Object.entries(existingActions).filter(
        ([key]) => key !== removedDay.toString()
      )
    );

    setValue('actions', updatedActions, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const newErrors: Record<number, string> = {};
    Object.entries(dayInputErrors).forEach(([key, msg]) => {
      const k = Number(key);
      if (k === index) return;
      newErrors[k > index ? k - 1 : k] = msg;
    });
    setDayInputErrors(newErrors);
    clearErrors('actions');
  };

  return (
    <Stack spacing={3} width="100%">
      <Stack spacing={1}>
        <FormSectionHeader description="Any actions assigned to timeline will process on day provided.">
          Set Process Timeline *{' '}
        </FormSectionHeader>

        <Stack spacing={1}>
          <Button
            variant="outlined"
            onClick={() => {
              addCustomDay();
              clearErrors('customDays');
            }}
            startIcon={<Add />}
            sx={{
              alignSelf: 'start',
              textTransform: 'none',
              color: theme.palette.semantic.text.title2,
              borderColor: theme.palette.semantic.text.title2,
              width: 250,
              mt: 0,
            }}
          >
            {customDays.length > 0
              ? 'Add Another Day to Timeline'
              : 'Add Day to Timeline'}
          </Button>
          <Controller
            name="customDays"
            control={control}
            render={() => (
              <>
                {errors.customDays &&
                  typeof errors.customDays.message === 'string' && (
                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                      {errors.customDays.message}
                    </Typography>
                  )}
              </>
            )}
          />

          {customDays.length > 0 && (
            <Stack spacing={1}>
              {customDays.map((dayValue, index) => (
                <Card
                  key={`custom-day-${index}-${dayValue}`}
                  variant="outlined"
                  sx={{
                    width: 280,
                    pt: 1,
                    mt: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    border: '0px',
                  }}
                >
                  <TextField
                    fullWidth
                    type="number"
                    label={
                      dayValue === 0
                        ? 'First Run'
                        : dayValue > 1
                        ? 'Days Out'
                        : 'Day Out'
                    }
                    value={dayInputs[index]}
                    onChange={(e) => onInputChange(index, e.target.value)}
                    onBlur={() => onDayInputBlur(index)}
                    error={!!dayInputErrors[index]}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: 364,
                      },
                    }}
                    sx={{
                      width: 250,
                    }}
                    helperText={dayInputErrors[index] ?? null}
                  />
                  {dayValue !== 0 && (
                    <IconButton
                      size="small"
                      onClick={() => onRemove(index)}
                      sx={{ mt: '4px' }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack spacing={1} width="70%">
        <FormSectionHeader
          description=" All actions have the option to email users or groups upon completion.
          You can set this notification preference for all selected actions
          here, or alternatively, you can customize this setting within each
          action's individual configuration."
        >
          Set Default Emails to Notify
        </FormSectionHeader>
      </Stack>
      <Stack direction="column" spacing={1} alignItems="flex-start">
        <Controller
          name="defaultUserToNotify"
          control={control}
          render={() => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: 400,
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <SharedAutocomplete
                  sources={['users', 'groups']}
                  autocompleteValue={autoCompleteResponseDefaultUsersToNotify}
                  setAutoCompleteValue={(val) => {
                    setAutoCompleteResponseDefaultUsersToNotify(val);

                    if (val && !defaultUserToNotify.includes(val)) {
                      const updatedUsers = [...defaultUserToNotify, val];
                      setValue('defaultUserToNotify', updatedUsers, {
                        shouldDirty: true,
                      });
                      setAutoCompleteResponseDefaultUsersToNotify('');
                      autocompleteKey.current = new Date().getTime().toString();
                      clearErrors('defaultUserToNotify');
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        />

        {defaultUserToNotify?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
              mt: 0,
            }}
          >
            {defaultUserToNotify.map((email) => (
              <AvatarChip
                key={email}
                type="Unknown"
                email={email}
                onDelete={() =>
                  setValue(
                    'defaultUserToNotify',
                    defaultUserToNotify.filter((e) => e !== email),
                    { shouldDirty: true }
                  )
                }
                size="small"
              />
            ))}
          </Box>
        )}
      </Stack>
      <Divider sx={{ width: '100%' }} />
      <Stack spacing={1} width="70%">
        <FormSectionHeader
          description=" Certain actions require a destination user to execute the process. You
          have two options for setting the default user: either specify a user
          or their manager (as defined in the user's profile) here, or configure
          the destination user within each action's settings."
        >
          Set Default Destination User
        </FormSectionHeader>

        <FormControl sx={{ pt: 1 }}>
          <RadioGroup
            name="radio-buttons-group"
            value={defaultRadio}
            onChange={async (e) => {
              const selected = e.target.value as typeof defaultRadio;

              if (selected === 'manager') {
                setValue('defaultUseManagerEmail', true, { shouldDirty: true });
                setValue('defaultDestUser', null, { shouldDirty: true });
                clearErrors('defaultDestUser');
              } else if (selected === 'custom') {
                setValue('defaultUseManagerEmail', false, {
                  shouldDirty: true,
                });
                const destUser = watch('defaultDestUser');
                if (!destUser) {
                  setValue('defaultDestUser', null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                } else {
                  setValue('defaultDestUser', destUser, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              } else {
                setValue('defaultUseManagerEmail', null, { shouldDirty: true });
                setValue('defaultDestUser', null, { shouldDirty: true });
                clearErrors('defaultDestUser');
              }
            }}
          >
            <FormControlLabel
              value="no-default"
              control={<Radio />}
              label="No default destination user"
              labelPlacement="end"
              sx={{ alignItems: 'center', pl: 0, ml: 0, width: 'fit-content' }}
            />

            <FormControlLabel
              value="manager"
              control={<Radio />}
              label="Manager"
              labelPlacement="end"
              sx={{ alignItems: 'center', pl: 0, ml: 0, width: 'fit-content' }}
            />

            <Stack>
              <Box>
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Custom User:"
                  sx={{
                    alignItems: 'center',
                    pl: 0,
                    ml: 0,
                    width: 'fit-content',
                  }}
                />
              </Box>
              <Box sx={{ pt: 1 }}>
                <Controller
                  name="defaultDestUser"
                  control={control}
                  render={() => (
                    <Box sx={{ width: 400 }}>
                      {defaultDestUser?.userEmail ? (
                        <AvatarChip
                          type="User"
                          email={defaultDestUser?.userEmail || ''}
                          onDelete={async () => {
                            setValue('defaultDestUser', null, {
                              shouldDirty: true,
                            });
                          }}
                          size="small"
                        />
                      ) : (
                        <UsersAutocomplete
                          autocompleteValue={defaultDestUser?.userEmail || ''}
                          setAutoCompleteValue={(newVal) => {
                            setValue(
                              'defaultDestUser',
                              newVal
                                ? {
                                    userEmail: newVal,
                                    role: 'owner',
                                    type: 'user',
                                  }
                                : null,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              }
                            );
                          }}
                          errors={!!errors.defaultDestUser}
                          helperText={errors.defaultDestUser?.message || ' '}
                          disabled={defaultRadio !== 'custom'}
                        />
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Stack>
          </RadioGroup>
        </FormControl>
      </Stack>
    </Stack>
  );
}
